import {
  User,
  Settings,
  Info,
  Check,
  AlertCircle,
  Bell,
  Mail,
  Search,
  Home,
  Star,
  Heart,
  Plus,
  FileText,
  Shield,
  DollarSign,
} from 'lucide-react';
import { Icon, Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import { SizeVariantsGrid } from '../SizeVariantsGrid';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
const colors = ['inherit', 'primary', 'secondary', 'muted', 'error', 'success', 'warning'] as const;

const sizeDescriptions: Record<(typeof sizes)[number], string> = {
  xs: '14px - Inline with small text',
  sm: '16px - Buttons, form fields',
  md: '20px - Standard icons',
  lg: '24px - Emphasis, headers',
  xl: '32px - Empty states, features',
};

function IconShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="icon">
          <Icon
            icon={Shield}
            size={playgroundProps.size as 'xs' | 'sm' | 'md' | 'lg' | 'xl'}
            color={
              playgroundProps.color as
                | 'inherit'
                | 'primary'
                | 'secondary'
                | 'muted'
                | 'error'
                | 'success'
                | 'warning'
            }
          />
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Icons come in 5 sizes based on their context of use"
       
      >
          <SizeVariantsGrid
            entries={sizes.map((size) => ({
              size,
              dimension: sizeDescriptions[size].split(' - ')[0]!,
              description: sizeDescriptions[size].split(' - ')[1]!,
              renderComponent: () => <Icon icon={Bell} size={size} />,
            }))}
          />
      </ShowcaseDemoSection>

      {/* Color Variants */}
      <ShowcaseDemoSection
        title="Color Variants"
        description="Icons support semantic colors for different states"
       
      >
          <HStack gap={6} wrap="wrap">
            {colors.map((color) => (
              <VStack key={color} gap={2} align="center">
                <Icon icon={AlertCircle} size="lg" color={color} />
                <Text size="xs" color="muted">
                  {color}
                </Text>
              </VStack>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Usage with Text */}
      <ShowcaseDemoSection
        title="Usage with Text"
        description="Icons commonly appear alongside text in UI elements"
       
      >
          <VStack gap={4}>
            <HStack gap={2} align="center">
              <Icon icon={User} size="sm" color="primary" />
              <Text>John Smith - Policyholder</Text>
            </HStack>

            <HStack gap={2} align="center">
              <Icon icon={Mail} size="sm" color="secondary" />
              <Text>john.smith@example.com</Text>
            </HStack>

            <HStack gap={2} align="center">
              <Icon icon={FileText} size="sm" color="primary" />
              <Text>Policy #AUTO-2024-001234</Text>
            </HStack>

            <HStack gap={2} align="center">
              <Icon icon={DollarSign} size="sm" color="success" />
              <Text color="success">Payment received</Text>
            </HStack>

            <HStack gap={2} align="center">
              <Icon icon={AlertCircle} size="sm" color="error" />
              <Text color="error">Coverage lapsed</Text>
            </HStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Icon Gallery */}
      <ShowcaseDemoSection
        title="Common Icons"
        description="A selection of commonly used icons in insurance applications"
       
      >
          <HStack gap={6} wrap="wrap">
            {[
              { icon: Home, label: 'Home' },
              { icon: User, label: 'User' },
              { icon: Shield, label: 'Shield' },
              { icon: FileText, label: 'Document' },
              { icon: DollarSign, label: 'Payment' },
              { icon: Check, label: 'Check' },
              { icon: AlertCircle, label: 'Alert' },
              { icon: Info, label: 'Info' },
              { icon: Bell, label: 'Notifications' },
              { icon: Search, label: 'Search' },
              { icon: Settings, label: 'Settings' },
              { icon: Plus, label: 'Add' },
              { icon: Star, label: 'Favorite' },
              { icon: Heart, label: 'Heart' },
              { icon: Mail, label: 'Mail' },
            ].map(({ icon, label }) => (
              <VStack key={label} gap={1} align="center" className="w-16">
                <Icon icon={icon} size="md" />
                <Text size="xs" color="muted">
                  {label}
                </Text>
              </VStack>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Accessible Standalone Icons */}
      <ShowcaseDemoSection
        title="Accessible Standalone Icons"
        description="Icons with aria-label for screen reader accessibility"
       
      >
          <VStack gap={4}>
            <HStack gap={4}>
              <Button variant="ghost" aria-label="Search policies">
                <Icon icon={Search} size="md" aria-label="Search" />
              </Button>
              <Button variant="ghost" aria-label="View notifications">
                <Icon icon={Bell} size="md" aria-label="Notifications" />
              </Button>
              <Button variant="ghost" aria-label="Open settings">
                <Icon icon={Settings} size="md" aria-label="Settings" />
              </Button>
            </HStack>
            <Text size="sm" color="muted">
              These icons have aria-label and will be announced by screen readers
            </Text>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const IconShowcase: ShowcaseDefinition = {
  id: 'icon',
  name: 'Icon',
  category: 'display',
  description: 'Icon wrapper component for consistent icon sizing and styling',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['xs', 'sm', 'md', 'lg', 'xl'],
      defaultValue: 'md',
      description: 'Size of the icon (xs=14px, sm=16px, md=20px, lg=24px, xl=32px)',
    },
    {
      name: 'color',
      type: 'select',
      options: ['inherit', 'primary', 'secondary', 'muted', 'error', 'success', 'warning'],
      defaultValue: 'inherit',
      description: 'Color of the icon',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-hidden="true" for decorative icons (default)',
      'aria-label for standalone meaningful icons',
      'role="img" when aria-label is provided',
    ],
    keyboardNav: [],
    notes: [
      'Decorative icons are hidden from screen readers by default',
      'Provide aria-label for icons that convey meaning without text',
      'Icons in buttons should have the button labeled, not the icon',
    ],
  },
  component: IconShowcaseComponent,
};
